import React, { useState, useEffect } from 'react';
import { hrmAPI } from '../../lib/api';
import { Plus, Edit, Trash2, Search, RefreshCw } from 'lucide-react';

const EmployeeDocuments = () => {
    const [documents, setDocuments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [formData, setFormData] = useState({
        employee_id: '',
        document_type: '',
        document_number: '',
        issue_date: '',
        expiry_date: ''
    });

    const documentTypes = ['NID', 'Passport', 'Driving License', 'Birth Certificate', 'Other'];

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [docRes, empRes] = await Promise.all([
                hrmAPI.getEmployeeDocuments(),
                hrmAPI.getEmployees()
            ]);
            setDocuments(docRes.data.data || docRes.data);
            setEmployees(empRes.data.data || empRes.data);
        } catch (err) { setError('Failed to load data'); }
        finally { setLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await hrmAPI.updateEmployeeDocument(editingId, formData);
            } else {
                await hrmAPI.createEmployeeDocument(formData);
            }
            setShowModal(false);
            resetForm();
            fetchData();
        } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    };

    const handleEdit = (doc) => {
        setFormData({
            employee_id: doc.employee_id || '',
            document_type: doc.document_type || '',
            document_number: doc.document_number || '',
            issue_date: doc.issue_date || '',
            expiry_date: doc.expiry_date || ''
        });
        setEditingId(doc.id);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure?')) return;
        try {
            await hrmAPI.deleteEmployeeDocument(id);
            fetchData();
        } catch (err) { setError('Failed to delete'); }
    };

    const resetForm = () => {
        setFormData({
            employee_id: '',
            document_type: '',
            document_number: '',
            issue_date: '',
            expiry_date: ''
        });
        setEditingId(null);
    };

    const getEmployeeName = (id) => employees.find(e => e.id === id)?.name || '-';

    const filteredDocuments = documents.filter(doc => 
        getEmployeeName(doc.employee_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.document_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.document_number?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Employee Documents</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Manage employee documents</p>
                </div>
                <button onClick={() => { resetForm(); setShowModal(true); }} className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
                    <Plus className="w-4 h-4 mr-2" /> Add Document
                </button>
            </div>

            {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}

            <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200" />
            </div>

            <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Employee</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Number</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Issue Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Expiry Date</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                {filteredDocuments.length > 0 ? filteredDocuments.map(doc => (
                                    <tr key={doc.id} className="hover:bg-gray-50 dark:hover:bg-slate-600">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200">{getEmployeeName(doc.employee_id)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{doc.document_type}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{doc.document_number}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{doc.issue_date || '-'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{doc.expiry_date || '-'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                            <button onClick={() => handleEdit(doc)} className="text-indigo-600 hover:text-indigo-900 mr-3"><Edit className="w-4 h-4" /></button>
                                            <button onClick={() => handleDelete(doc.id)} className="text-red-600 hover:text-red-900"><Trash2 className="w-4 h-4" /></button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No records found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-6 w-full max-w-md mx-4">
                        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">{editingId ? 'Edit' : 'Add'} Document</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Employee</label>
                                <select value={formData.employee_id} onChange={(e) => setFormData({...formData, employee_id: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required>
                                    <option value="">Select Employee</option>
                                    {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Document Type</label>
                                <select value={formData.document_type} onChange={(e) => setFormData({...formData, document_type: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required>
                                    <option value="">Select Type</option>
                                    {documentTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Document Number</label>
                                <input type="text" value={formData.document_number} onChange={(e) => setFormData({...formData, document_number: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required />
                            </div>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Issue Date</label>
                                    <input type="date" value={formData.issue_date} onChange={(e) => setFormData({...formData, issue_date: e.target.value})} 
                                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expiry Date</label>
                                    <input type="date" value={formData.expiry_date} onChange={(e) => setFormData({...formData, expiry_date: e.target.value})} 
                                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-100 dark:hover:bg-slate-600">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeDocuments;
